'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Skeleton from '@mui/material/Skeleton';
import GeneralSection from '@/components/seo/GeneralSection';
import JobTitleSearch from '@/components/seo/search-area/SearchBox';
import CitySearch from './RankingCitySearch';
import ListOptions from './ListOptions';
import SalaryOverview from '@/components/seo/job-title/salary-insight/SalaryOverview';
import LinksContainer from '@/components/seo/LinksContainer';
import { RankPageProps } from '@/types/job-ranking';
import { JobTitleType, SearchType } from '@/types/job-title';
import {
  JOB_TITLE_PAGE_ROUTER_PATH,
  JOB_RANKING_PAGE_ROUTER_PATH,
} from '@/constants/router-path';
import { renameL1Name } from '@/utils/job-ranking';
import { joinStrFromList } from '@/utils/common-utils';
import { getJobTitleData } from '@/api/tanzhi';

const RankPage = (props: RankPageProps) => {
  const { rankData, filterId, levelOneData, seoHotLinks } = props;
  const {
    location,
    exist_locations,
    salary_rank,
    open_job_rank,
    job_title_name,
  } = rankData;

  const cityPrefix = location ? `${location.name}·` : '';
  const categoryPrefix = renameL1Name(filterId, job_title_name);

  const [jobTitle, setJobTitle] = React.useState<
    JobTitleType | null | undefined
  >(undefined);
  const [loading, setLoading] = React.useState(false);
  const router = useRouter();

  const handleJobSearch = async (value: SearchType) => {
    if (!value || value.id === jobTitle?.id) return;
    setLoading(true);
    try {
      const { data: jobTitleData } = await getJobTitleData(
        (value.name_type === 'alias' && value.alias_id) || value.id,
        location?.pinyin,
      );
      setJobTitle(jobTitleData || null);
    } catch (error) {
      setJobTitle(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mt-12">
        <div className="flex items-center justify-between gap-10 mb-10">
          <div className="grow">
            <JobTitleSearch
              onChange={handleJobSearch}
              enableAnimation={false}
              inputBorderColor="border border-web-blue-500"
              className="w-full"
              showEndAdornment={false}
              placeholder="搜索具体职位"
            />
          </div>
          <CitySearch
            options={exist_locations}
            location={location}
            filterId={filterId}
          />
        </div>
        <h2 className="text-xl font-semibold mb-4">热门岗位分类</h2>
        <ul className="flex flex-wrap list-none gap-y-4 !ml-0">
          {levelOneData.map((item) => (
            <li key={item.id}>
              <Link
                data-selected={item.id === filterId}
                href={`${JOB_RANKING_PAGE_ROUTER_PATH}${location ? `${location.pinyin}_${item.id}` : item.id}/`}
                className="block px-4 py-2 rounded transition-colors duration-250 hover:bg-web-blue-500/4 hover:text-web-blue-500 hover:font-semibold data-[selected=true]:bg-web-blue-500/4 data-[selected=true]:text-web-blue-500 data-[selected=true]:font-semibold"
              >
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <GeneralSection marginTop="mt-10">
        {loading ? (
          <Skeleton
            className="mb-8"
            variant="rounded"
            width="100%"
            height={200}
          />
        ) : jobTitle ? (
          <SalaryOverview
            margin="mb-8"
            salaryData={jobTitle.job_open_details.seo_content['平均月薪'] || {}}
            salaryDetails={jobTitle.salary_details}
            jobOpenDetails={jobTitle.job_open_details}
            border="cursor-pointer border border-web-tertiary hover:border-web-blue-500"
            handleClick={() =>
              router.push(
                `${location ? `/${location.pinyin}/` : JOB_TITLE_PAGE_ROUTER_PATH}${jobTitle.id}/`,
              )
            }
            avgSalaryTitle={
              <Link
                href={`${location ? `/${location.pinyin}/` : JOB_TITLE_PAGE_ROUTER_PATH}${jobTitle.id}/`}
                className="hover:underline text-web-blue-500"
              >
                {joinStrFromList(
                  [location?.name, jobTitle.display_name, '平均月薪'],
                  '·',
                )}
              </Link>
            }
          />
        ) : jobTitle === null ? (
          <div className="text-center mb-8">
            <span className="font-semibold text-xl">暂无结果</span>
            <p className="mt-1 text-sm text-web-grey-600">
              请尝试更换其他关键词搜索
            </p>
          </div>
        ) : (
          <></>
        )}
        <ListOptions
          title={`${cityPrefix}${categoryPrefix}薪资收入TOP10的职位`}
          subTitle={`锁定【${job_title_name}】岗的高薪职位，为你的求职谈判与职业规划提供参考。数据涨幅基于近 6 个月数据统计。`}
          options={salary_rank}
          filterId={filterId}
          location={location}
        />
        <ListOptions
          title={`${cityPrefix}${categoryPrefix}岗位需求热榜`}
          subTitle={`洞察招聘需求最旺盛的【${job_title_name}】职位，把握市场最新动向。`}
          options={open_job_rank}
          filterId={filterId}
          location={location}
          isOpenJob={true}
        />
        <LinksContainer seoHotLinks={seoHotLinks} city={location} />
      </GeneralSection>
    </>
  );
};

export default RankPage;
